// Scroll Image Transition - ES Module
class ScrollImageTransition {
  constructor(element) {
    this.element = element;
    this.images = element.querySelectorAll('.scroll-image-transition__image');
    this.features = element.querySelectorAll('.scroll-image-transition__feature');
    this.currentImageIndex = 0;
    this.isScrolling = false;
    if (this.images.length <= 1 || this.features.length === 0) return;
    this.init();
  }

  init() {
    console.log('[ScrollImageTransition] Initializing', {
      element: this.element,
      classes: this.element.className,
      isStaticImage: this.element.classList.contains('scroll-image-transition--anim-static-image'),
      imagesCount: this.images.length,
      featuresCount: this.features.length
    });
    
    this.setupIntersectionObserver();
    this.setupClickHandlers();
    this.updateImageState(0);
    this.updateFeatureState(0);
    
    // Cache initial feature positions for static image mode
    if (this.element.classList.contains('scroll-image-transition--anim-static-image')) {
      console.log('[ScrollImageTransition] Static image mode detected - caching positions');
      this.cacheFeaturePositions();
    }

    if (this.element.classList.contains('scroll-image-transition--anim-oval')) {
      this.images.forEach((img, idx) => {
        img.style.opacity = '1';
        img.style.zIndex = idx === 0 ? '2' : '1';
        img.style.clipPath = idx === 0 ? 'inset(0 0 0 0)' : 'inset(100% 0 0 0)';
      });
    }
  }

  cacheFeaturePositions() {
    const featuresContainer = this.element.querySelector('.scroll-image-transition__features');
    if (!featuresContainer) {
      console.warn('[ScrollImageTransition] featuresContainer not found');
      return;
    }
    
    console.log('[ScrollImageTransition] Caching feature positions...', {
      container: featuresContainer,
      featuresCount: this.features.length
    });
    
    // Temporarily reset transform to get natural positions
    const originalTransform = featuresContainer.style.transform;
    featuresContainer.style.transform = 'translateY(0)';
    
    // Force reflow
    void featuresContainer.offsetHeight;
    
    // Get gap value
    const gapStyle = getComputedStyle(featuresContainer).gap;
    const gapMatch = gapStyle.match(/([\d.]+)(px|rem|em)/);
    let gap = 0;
    if (gapMatch) {
      const gapValue = parseFloat(gapMatch[1]);
      const unit = gapMatch[2];
      if (unit === 'rem') {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        gap = gapValue * rootFontSize;
      } else if (unit === 'em') {
        const parentFontSize = parseFloat(getComputedStyle(featuresContainer).fontSize);
        gap = gapValue * parentFontSize;
      } else {
        gap = gapValue;
      }
    }
    
    // Cache positions including gaps
    this.featurePositions = [];
    let cumulativeTop = 0;
    this.features.forEach((feature, index) => {
      if (feature) {
        this.featurePositions.push({
          top: cumulativeTop,
          height: feature.offsetHeight
        });
        // Add gap after this feature (except for the last one)
        if (index < this.features.length - 1) {
          cumulativeTop += feature.offsetHeight + gap;
        } else {
          cumulativeTop += feature.offsetHeight;
        }
      }
    });
    
    // Restore transform
    featuresContainer.style.transform = originalTransform;
  }

  setupIntersectionObserver() {
    const frameCountValue = getComputedStyle(this.element).getPropertyValue('--frame-count') || '';
    const frames = Math.max(
      1,
      parseInt(frameCountValue) || this.images.length
    );
    let ticking = false;

    const easeIn = (t) => t * t * t;
    const isOval = this.element.classList.contains('scroll-image-transition--anim-oval');
    const isStaticImage = this.element.classList.contains('scroll-image-transition--anim-static-image');

    const updateByScroll = () => {
      if (this.isScrolling) return;

      const rect = this.element.getBoundingClientRect();
      const vh = window.innerHeight;
      const totalScrollable = this.element.offsetHeight - vh;
      if (totalScrollable <= 0) return;

      const y = -rect.top;
      const clamped = Math.max(0, Math.min(totalScrollable, y));
      const progress = (clamped / totalScrollable) * (frames - 1);
      const baseIndex = Math.floor(progress);
      const t = progress - baseIndex;

      const activeTextIndex = t >= 0.5 ? Math.min(baseIndex + 1, this.features.length - 1) : baseIndex;
      this.updateFeatureState(activeTextIndex);

      const currentIdx = baseIndex;
      const nextIdx = Math.min(baseIndex + 1, this.images.length - 1);

      const blendFeatures = (p) => {
        const base = 0.6;
        const delta = 0.4;
        this.features.forEach((feat, idx) => {
          if (idx === currentIdx) feat.style.opacity = String(1 - delta * p);
          else if (idx === nextIdx) feat.style.opacity = String(base + delta * p);
          else feat.style.opacity = String(base);
        });
      };

      if (isStaticImage) {
        // Static image mode: only animate text features, no image transitions
        const incoming = easeIn(t);
        blendFeatures(incoming);
        
        // Calculate vertical offset to center the active feature in the wrapper
        const featuresContainer = this.element.querySelector('.scroll-image-transition__features');
        const featuresWrapper = this.element.querySelector('.scroll-image-transition__features-wrapper');
        
        console.log('[ScrollImageTransition] Static image mode active', {
          hasContainer: !!featuresContainer,
          hasWrapper: !!featuresWrapper,
          featuresCount: this.features.length,
          activeTextIndex,
          progress: t,
          baseIndex
        });
        
        if (featuresContainer && featuresWrapper && this.features.length > 0) {
          const activeFeature = this.features[activeTextIndex];
          if (activeFeature) {
            // Ensure positions are cached
            if (!this.featurePositions || this.featurePositions.length === 0) {
              console.log('[ScrollImageTransition] Caching feature positions...');
              this.cacheFeaturePositions();
              console.log('[ScrollImageTransition] Cached positions:', this.featurePositions);
            }
            
            // Get wrapper dimensions
            const wrapperHeight = featuresWrapper.clientHeight;
            const wrapperRect = featuresWrapper.getBoundingClientRect();
            
            console.log('[ScrollImageTransition] Wrapper dimensions:', {
              clientHeight: wrapperHeight,
              offsetHeight: featuresWrapper.offsetHeight,
              rectHeight: wrapperRect.height,
              rectTop: wrapperRect.top
            });
            
            if (wrapperHeight === 0) {
              console.warn('[ScrollImageTransition] Wrapper height is 0 - cannot calculate transform');
              return;
            }
            
            let featureTop, featureHeight;
            
            // Use cached positions if available
            if (this.featurePositions && this.featurePositions[activeTextIndex]) {
              featureTop = this.featurePositions[activeTextIndex].top;
              featureHeight = this.featurePositions[activeTextIndex].height;
              console.log('[ScrollImageTransition] Using cached position:', { featureTop, featureHeight });
            } else {
              // Fallback: calculate on the fly
              console.warn('[ScrollImageTransition] Using fallback calculation for index', activeTextIndex);
              featureTop = 0;
              const gapStyle = getComputedStyle(featuresContainer).gap;
              const gapMatch = gapStyle.match(/([\d.]+)(px|rem|em)/);
              let gap = 0;
              if (gapMatch) {
                const gapValue = parseFloat(gapMatch[1]);
                const unit = gapMatch[2];
                if (unit === 'rem') {
                  gap = gapValue * parseFloat(getComputedStyle(document.documentElement).fontSize);
                } else if (unit === 'em') {
                  gap = gapValue * parseFloat(getComputedStyle(featuresContainer).fontSize);
                } else {
                  gap = gapValue;
                }
              }
              
              for (let i = 0; i < activeTextIndex; i++) {
                if (this.features[i]) {
                  featureTop += this.features[i].offsetHeight;
                  if (i < activeTextIndex - 1) {
                    featureTop += gap;
                  }
                }
              }
              featureHeight = activeFeature.offsetHeight;
              console.log('[ScrollImageTransition] Fallback calculation:', { featureTop, featureHeight, gap });
            }
            
            // Calculate where we want the feature to be (centered in wrapper)
            const wrapperCenter = wrapperHeight / 2;
            const featureCenter = featureTop + (featureHeight / 2);
            
            // Calculate transform to center the feature
            const translateY = wrapperCenter - featureCenter;
            
            console.log('[ScrollImageTransition] Transform calculation:', {
              wrapperHeight,
              wrapperCenter,
              featureTop,
              featureHeight,
              featureCenter,
              translateY,
              currentTransform: featuresContainer.style.transform
            });
            
            featuresContainer.style.transform = `translateY(${translateY}px)`;
            
            // Verify it was applied
            const appliedTransform = getComputedStyle(featuresContainer).transform || 'none';
            console.log('[ScrollImageTransition] Applied transform:', appliedTransform);
          } else {
            console.warn('[ScrollImageTransition] Active feature not found at index', activeTextIndex);
          }
        } else {
          console.warn('[ScrollImageTransition] Missing required elements:', {
            featuresContainer: !!featuresContainer,
            featuresWrapper: !!featuresWrapper,
            featuresLength: this.features.length
          });
        }
        
        this.currentImageIndex = activeTextIndex;
      } else if (isOval) {
        const reveal = easeIn(t);
        const topInset = 100 - Math.round(reveal * 100);
        this.images.forEach((img, idx) => {
          img.style.opacity = '1';
          img.style.zIndex = idx === nextIdx ? '2' : idx === currentIdx ? '1' : '0';
          if (idx === nextIdx) {
            img.style.clipPath = `inset(${topInset}% 0 0 0)`;
            img.classList.add('scroll-image-transition__image--active');
          } else {
            img.style.clipPath = 'inset(0 0 0 0)';
            if (idx !== currentIdx) img.classList.remove('scroll-image-transition__image--active');
          }
        });
        if (currentIdx === nextIdx) {
          const last = nextIdx;
          this.images.forEach((img, idx) => {
            if (idx === last) {
              img.style.clipPath = 'inset(0 0 0 0)';
              img.style.zIndex = '2';
            }
          });
        }
        blendFeatures(reveal);
      } else {
        const incoming = easeIn(t);
        const outgoing = 1 - incoming;
        this.images.forEach((img, idx) => {
          let opacity = 0;
          if (idx === currentIdx) opacity = outgoing;
          if (idx === nextIdx) opacity = Math.max(opacity, incoming);
          img.style.opacity = String(opacity);
          img.style.clipPath = '';
          if (opacity > 0.01) img.classList.add('scroll-image-transition__image--active');
          else img.classList.remove('scroll-image-transition__image--active');
        });
        blendFeatures(incoming);
      }
      this.currentImageIndex = activeTextIndex;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateByScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    this._cleanupScroll = () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };

    updateByScroll();
  }

  setupClickHandlers() {
    this.features.forEach((feature) => {
      feature.addEventListener('click', () => {
        this.scrollToFeature(feature);
      });
      feature.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.scrollToFeature(feature);
        }
      });
    });
  }

  scrollToFeature(feature) {
    const rect = feature.getBoundingClientRect();
    const viewportCenter = window.innerHeight / 2;
    const elementCenter = rect.top + rect.height / 2;
    const scrollOffset = elementCenter - viewportCenter;
    this.isScrolling = true;
    window.scrollBy({ top: scrollOffset, behavior: 'smooth' });
    const featureIndex = parseInt(feature.dataset.featureIndex, 10);
    const imageIndex = parseInt(feature.dataset.imageIndex, 10);
    this.updateFeatureState(featureIndex);
    this.updateImageState(imageIndex);
    setTimeout(() => {
      this.isScrolling = false;
    }, 1000);
  }

  updateImageState(imageIndex) {
    if (imageIndex === this.currentImageIndex) return;
    this.currentImageIndex = imageIndex;
    this.images.forEach((image) => {
      const idx = parseInt(image.dataset.imageIndex, 10);
      image.style.opacity = idx === imageIndex ? '1' : '0';
      image.classList.toggle('scroll-image-transition__image--active', idx === imageIndex);
    });
  }

  updateFeatureState(featureIndex) {
    this.features.forEach((feature) => {
      const featureIndexAttr = parseInt(feature.dataset.featureIndex, 10);
      if (featureIndexAttr === featureIndex) {
        feature.classList.add('scroll-image-transition__feature--active');
      } else {
        feature.classList.remove('scroll-image-transition__feature--active');
      }
    });
  }

  destroy() {
    if (this._cleanupScroll) this._cleanupScroll();
  }
}

// Auto-init
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.scroll-image-transition');
  sections.forEach((el) => new ScrollImageTransition(el));
});

// Re-initialize on theme editor changes
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.scroll-image-transition');
    if (section) {
      new ScrollImageTransition(section);
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.scroll-image-transition');
    if (section && section.scrollImageTransition) {
      section.scrollImageTransition.destroy();
    }
  });
}

export {};
